import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateProductsStation,
  useUpdateProductsStation,
} from '@/api/generated/products-stations/products-stations';
import {
  buildStationFormSchema,
  emptyStationFormValues,
  type StationFormValues,
} from './stationFormSchema';
import { toStationRequestBody } from './toStationRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  StationTypeCbId: 'stationType',
};

interface UseStationFormOptions {
  /** Bez `stationId` = zakládání, s `stationId` = editace. */
  stationId?: number;
  initialValues?: StationFormValues;
  /** Voláno po úspěšném uložení (předává id uložené stanice) — invalidace, toast; NEzavírá okno. */
  onSaved: (id: number) => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulářová logika stanice (sdílená pro zakládání i editaci) — TanStack Form se Zod validací,
 * create/update mutace a promítnutí serverových validačních chyb do polí. `submit()` vrací, zda se
 * uložení povedlo (pro „Uložit & zpět" ve `FormActionBar`).
 */
export function useStationForm({
  stationId,
  initialValues,
  onSaved,
  onError,
}: UseStationFormOptions) {
  const { t } = useTranslation('products');
  const schema = useMemo(() => buildStationFormSchema(t), [t]);

  const createMutation = useCreateProductsStation();
  const updateMutation = useUpdateProductsStation();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyStationFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toStationRequestBody(value);
      try {
        if (stationId != null) {
          await updateMutation.mutateAsync({ id: stationId, data });
          onSaved(stationId);
        } else {
          const newId = await createMutation.mutateAsync({ data });
          onSaved(newId);
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu. */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}

/** Typ instance TanStack Form pro detail stanice (pro propsy podřízených komponent). */
export type StationFormApi = ReturnType<typeof useStationForm>['form'];
